import { Link } from 'react-router-dom';
import { useScrollReveal } from '../../../hooks/useScrollReveal';
import { contactInfo } from '../../../mocks/contacto';
import { getAdminData } from '../../../hooks/useAdminData';

interface ContactInfoData {
  address: string;
  city: string;
  postalCode: string;
  region: string;
  country: string;
  phone: string;
  email: string;
  mapLink: string;
  altaCosturaSchedule: string;
  arreglosSchedule: string;
  tiendaSchedule: string;
}

export default function ContactInfo() {
  const { ref, isVisible, style } = useScrollReveal({ threshold: 0.1, distance: 36, duration: 900 });

  const adminContact = getAdminData<ContactInfoData>('admin_contact_info', {
    address: contactInfo.address,
    city: contactInfo.city,
    postalCode: contactInfo.postalCode,
    region: contactInfo.region,
    country: contactInfo.country,
    phone: contactInfo.phone,
    email: contactInfo.email,
    mapLink: contactInfo.mapLink,
    altaCosturaSchedule: contactInfo.hours.altaCostura.schedule,
    arreglosSchedule: contactInfo.hours.arreglos.schedule,
    tiendaSchedule: contactInfo.hours.tienda.schedule,
  });

  const infoItems = [
    {
      icon: 'ri-map-pin-line',
      label: 'Dirección',
      lines: [
        adminContact.address,
        `${adminContact.city}, ${adminContact.country}`,
      ],
      action: {
        label: 'Ver en Google Maps',
        href: adminContact.mapLink,
        external: true,
      },
    },
    {
      icon: 'ri-phone-line',
      label: 'Teléfono',
      lines: [adminContact.phone],
      action: {
        label: 'Llamar ahora',
        href: `tel:${adminContact.phone.replace(/\s/g, '')}`,
        external: true,
      },
    },
    {
      icon: 'ri-mail-line',
      label: 'Email',
      lines: [adminContact.email],
      action: {
        label: 'Enviar email',
        href: `mailto:${adminContact.email}`,
        external: true,
      },
    },
  ];

  const hours = [
    { label: 'Consultas Alta Costura', schedule: adminContact.altaCosturaSchedule },
    { label: 'Arreglos Prêt-à-porter', schedule: adminContact.arreglosSchedule },
    { label: 'Tienda Online', schedule: adminContact.tiendaSchedule },
  ];

  return (
    <section ref={ref} className="w-full bg-white py-20 md:py-28">
      <div className="px-6 lg:px-[7%]">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Contact cards — stack vertically on large screens for breathing room */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-5" style={style}>
            {infoItems.map((item, index) => (
              <div
                key={item.label}
                className="group bg-brand-gray-50 rounded-3xl p-6 md:p-8 flex items-start gap-5 transition-colors hover:bg-brand-gray-100/60"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="w-14 h-14 rounded-full bg-brand-black flex items-center justify-center text-white flex-shrink-0">
                  <i className={`${item.icon} text-xl`} />
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="font-sans text-brand-black text-[11px] tracking-[0.2em] uppercase font-bold">
                    {item.label}
                  </h3>
                  <div className="mt-2 space-y-0.5">
                    {item.lines.map((line) => (
                      <p key={line} className="font-sans text-brand-gray-600 text-[15px] leading-relaxed">
                        {line}
                      </p>
                    ))}
                  </div>
                  <a
                    href={item.action.href}
                    target={item.action.external ? '_blank' : undefined}
                    rel={item.action.external ? 'noopener noreferrer' : undefined}
                    className="inline-flex items-center gap-1 font-sans text-brand-black text-xs tracking-wider uppercase mt-4 hover:text-brand-gray-500 transition-colors"
                  >
                    {item.action.label}
                    <i className="ri-arrow-right-line" />
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Hours + Quick links */}
          <div
            style={isVisible ? {
              opacity: 1,
              transform: 'translateY(0px)',
              transition: 'opacity 900ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 200ms, transform 900ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 200ms',
            } : {
              opacity: 0,
              transform: 'translateY(30px)',
              transition: 'opacity 900ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 200ms, transform 900ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 200ms',
              willChange: 'opacity, transform',
            }}
          >
            <h2 className="font-serif text-brand-black text-2xl md:text-3xl font-semibold">
              Horarios del <span className="italic text-brand-gray-300">Atelier</span>
            </h2>

            <div className="mt-8 space-y-0">
              {hours.map((h) => (
                <div
                  key={h.label}
                  className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-6 py-5 border-b border-brand-gray-100 last:border-b-0"
                >
                  <span className="font-sans text-brand-black text-sm font-medium sm:w-[240px] flex-shrink-0">
                    {h.label}
                  </span>
                  <span className="font-sans text-brand-gray-500 text-[15px]">
                    {h.schedule}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Link
                to="/alta-costura"
                className="group bg-brand-black text-white rounded-2xl p-6 hover:bg-brand-gray-800 transition-colors duration-300"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-sans text-brand-gray-400 text-[11px] tracking-[0.15em] uppercase">
                      Reservas
                    </span>
                    <p className="font-serif text-lg mt-1">Alta Costura</p>
                  </div>
                  <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
              <Link
                to="/pret-a-porter"
                className="group bg-brand-gray-50 rounded-2xl p-6 hover:bg-brand-gray-100 transition-colors duration-300"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-sans text-brand-gray-400 text-[11px] tracking-[0.15em] uppercase">
                      Servicios
                    </span>
                    <p className="font-serif text-brand-black text-lg mt-1">Arreglos</p>
                  </div>
                  <i className="ri-arrow-right-line text-brand-black text-xl group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}